import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import VialSvg from "./VialSvg";
import { useConfig } from "../lib/config";
import { useDrawer } from "../lib/DrawerContext";

// Backwards-compatible molecular fallback (used by NotFound page only)
export const MoleculeSvg = ({ seed = 1, color = "#00DF82", className = "" }) => (
  <VialSvg seed={seed} color={color} className={className} />
);

// Category → vial glow color + display label + bg tint
const CATEGORY_CONFIG = {
  "Metabólicos":           { color: "#00BFFF", tint: "rgba(0,191,255,0.28)",  label: "METABÓLICO / PÉRDIDA DE PESO" },
  "Cognitivos":            { color: "#A855F7", tint: "rgba(168,85,247,0.28)", label: "COGNITIVO / NEUROPÉPTIDOS"     },
  "Recuperación Tisular":  { color: "#00C896", tint: "rgba(0,200,150,0.28)",  label: "ANTI-AGING / CELULAR"          },
  "Regeneración":          { color: "#00C896", tint: "rgba(0,200,150,0.28)",  label: "ANTI-AGING / REGENERACIÓN"     },
  "Estéticos":             { color: "#00C896", tint: "rgba(0,200,150,0.28)",  label: "ANTI-AGING / ESTÉTICO"         },
  "Hormonal / GH":         { color: "#F59E0B", tint: "rgba(245,158,11,0.28)", label: "HORMONAL / GH"                 },
  "Bienestar Sexual":      { color: "#EC4899", tint: "rgba(236,72,153,0.28)", label: "BIENESTAR SEXUAL"              },
  "Inmunológico":          { color: "#00DF82", tint: "rgba(0,223,130,0.28)",  label: "INMUNOLÓGICO"                  },
};

const DEFAULT_CAT = { color: "#00DF82", tint: "rgba(0,223,130,0.28)", label: "INVESTIGACIÓN" };

const formatCRC = (price) => `₡${Math.round(price).toLocaleString("en-US")}`;

const ProductCard = ({ product, idx = 0 }) => {
  const cat = CATEGORY_CONFIG[product.category] || DEFAULT_CAT;
  const { config } = useConfig();
  const { openDrawer } = useDrawer();
  const showPrice = product.base_price > 0;

  return (
    <Link
      to={`/productos/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col border border-[#22222B] bg-[#12121A] rounded-2xl overflow-hidden transition-all duration-300"
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${cat.color}55`;
        e.currentTarget.style.boxShadow = `0 0 32px ${cat.color}14`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#22222B";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      {/* ─── Vial area ─────────────────────────────────────────── */}
      <div
        className="aspect-[4/3] relative overflow-hidden"
        style={{
          background: `radial-gradient(ellipse at 50% 70%, ${cat.tint} 0%, #0A0A0F 62%)`,
        }}
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={`${product.name} (${product.scientific_name}) — péptido sintético en Costa Rica`}
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <VialSvg
            seed={idx + 1}
            color={cat.color}
            label={product.name}
            className="absolute inset-0 w-full h-full"
            aria-label={`Vial de ${product.name} en Costa Rica`}
          />
        )}

        {/* Availability badge — top right */}
        {product.availability && product.availability !== "En Stock" && (
          <span
            className="absolute top-3 right-3 text-[8px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border"
            style={{
              color: cat.color,
              borderColor: `${cat.color}50`,
              background: `${cat.color}12`,
              backdropFilter: "blur(6px)",
            }}
          >
            {product.availability}
          </span>
        )}
      </div>

      {/* ─── Info section ───────────────────────────────────────── */}
      <div className="flex flex-col flex-1 p-5 gap-2">
        {/* Category label */}
        <span
          className="text-[9px] font-semibold tracking-[0.22em] uppercase"
          style={{ color: cat.color }}
        >
          {cat.label}
        </span>

        {/* Product name */}
        <h3 className="text-lg font-display font-semibold text-white leading-tight group-hover:text-[#00DF82] transition-colors duration-200">
          {product.name}
        </h3>

        {/* Short description */}
        <p className="text-sm text-[#8F8F99] line-clamp-2 flex-1 leading-relaxed">
          {product.short_description || product.scientific_name}
        </p>

        {/* Price */}
        {showPrice && (
          <div
            data-testid={`product-price-${product.slug}`}
            className="text-2xl font-bold tracking-tight mt-1"
            style={{ color: "#00C896" }}
          >
            {formatCRC(product.base_price)}
          </div>
        )}

        {/* ─── CTA row ───── */}
        <div className={`flex items-center gap-2 ${showPrice ? "pt-3" : "pt-3 mt-auto"} border-t border-[#22222B]`}>
          <button
            data-testid={`product-card-consult-btn-${product.slug}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDrawer(product); }}
            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-[#00C896] text-black font-bold text-[11px] uppercase tracking-wider hover:bg-white transition-colors duration-200"
          >
            <WhatsAppIcon size={13} />
            Pedir Ahora
          </button>

          {/* Product detail link */}
          <div
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full border border-[#2D2D38] group-hover:border-[#8F8F99] transition-colors duration-200"
          >
            <ArrowUpRight
              size={14}
              className="text-[#8F8F99] group-hover:text-white group-hover:rotate-12 transition-all duration-200"
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
